import { Alert, MenuItem, TextField } from '@mui/material';
import './fpCriteria.css';

const axisNames = { f: '위험 노출 빈도', p: '노출 시 사고 가능성' };
const likelihoodNames = ['매우 낮음', '낮음', '보통', '높음', '매우 높음'];

export function fpErrors(fp) {
  if (!fp) return [];
  const errors = [];
  for (let f = 0; f < 5; f += 1) for (let p = 0; p < 3; p += 1) {
    const l = fp.matrix?.[f]?.[p];
    if (!Number.isInteger(l) || l < 1 || l > 5) errors.push(`노출 빈도 ${f + 1}단계·사고 가능성 ${p + 1}단계: 종합 가능성을 선택하세요.`);
    else if ((f > 0 && l < fp.matrix[f - 1][p]) || (p > 0 && l < fp.matrix[f][p - 1])) errors.push(`노출 빈도 ${f + 1}단계·사고 가능성 ${p + 1}단계: 노출 빈도 또는 사고 가능성이 증가할 때 종합 가능성이 낮아질 수 없습니다.`);
  }
  for (const axis of ['f', 'p']) {
    if (fp[axis]?.some(row => !row.description?.trim() || row.description.length > 200)) errors.push(`${axisNames[axis]} 설명은 1~200자로 입력하세요.`);
  }
  if (!fp.rationale?.trim() || fp.rationale.length > 2000) errors.push('설정 근거를 1~2000자로 입력하세요.');
  return errors;
}

export function FpReference({ fp, selectedLevel }) {
  if (!fp?.version) return <div className="fp-reference"><p>종합 가능성 판단기준 미설정</p><p>기존 가능성은 직접 선택할 수 있습니다.</p></div>;
  return <div className="fp-reference">
    <div className="fp-heading"><strong>종합 가능성 판단표</strong><span>읽기 전용 · 기준 v{fp.version}</span></div>
    <table className="fp-table" aria-label="종합 가능성 판단표">
      <thead><tr><th scope="col">위험 노출 빈도 ↓<small>노출 시 사고 가능성 →</small></th>{[1, 2, 3].map(p => <th scope="col" key={p}>{p}단계</th>)}</tr></thead>
      <tbody>{(fp.f || []).map((row, f) => <tr key={row.level}><th scope="row">{row.level}단계 · {row.description}</th>
        {[0, 1, 2].map(p => <td key={p} className={Number(selectedLevel) === fp.matrix?.[f]?.[p] ? 'fp-match' : ''}>{fp.matrix?.[f]?.[p] == null ? '미설정' : `${fp.matrix[f][p]}단계`}</td>)}
      </tr>)}</tbody>
    </table>
    <dl className="fp-p-definitions" aria-label="노출 시 사고 가능성 기준">{(fp.p || []).map(row => <div key={row.level}><dt>{row.level}단계</dt><dd>{row.description}</dd></div>)}</dl>
    <p className="fp-rationale">설정 근거: {fp.rationale}</p>
  </div>;
}

export function FpCriteriaEditor({ value, editing, busy, onChange }) {
  if (!value) return <Alert severity="info">종합 가능성 판단기준을 지원하는 API로 업데이트해주세요.</Alert>;
  const editable = editing && value.available !== false;
  const updateDescription = (axis, index, description) => onChange({ ...value,
    [axis]: value[axis].map((row, i) => i === index ? { ...row, description } : row) });
  return <section className="fp-editor">
    <div className="fp-heading"><h2>종합 가능성 판단기준</h2><span>{value.version ? `기준 v${value.version}` : '미설정'}</span></div>
    {value.available === false && <Alert severity="warning">종합 가능성 판단기준 DB 변경 적용 후 편집할 수 있습니다. 기존 가능성·중대성 평가는 계속 사용할 수 있습니다.</Alert>}
    <div className="fp-editor-definitions">{Object.entries(axisNames).map(([axis, title]) => <div key={axis}>
      <h3>{title}</h3>{value[axis].map((row, index) => <div className="fp-definition-row" key={row.level}>
        <span>{row.level}단계</span>{editable ? <TextField fullWidth size="small" multiline maxRows={4} disabled={busy}
          value={row.description} inputProps={{ maxLength: 200, 'aria-label': `${title} ${row.level}단계 판단기준` }}
          onChange={event => updateDescription(axis, index, event.target.value)} /> : <span>{row.description}</span>}
      </div>)}
    </div>)}</div>
    <h3>종합 가능성 판단표</h3>
    <div className="fp-editor-table-scroll" role="region" aria-label="종합 가능성 판단표 영역" tabIndex={0}>
    <table className="fp-table" aria-label="종합 가능성 판단표">
      <thead><tr><th scope="col">위험 노출 빈도 ↓<small>노출 시 사고 가능성 →</small></th>{value.p.map(row => <th scope="col" key={row.level}>{row.level}단계<small>{row.description}</small></th>)}</tr></thead>
      <tbody>{value.f.map((row, f) => <tr key={row.level}><th scope="row">{row.level}단계<small>{row.description}</small></th>{[0, 1, 2].map(p => {
        const l = value.matrix[f][p];
        const inconsistent = l != null && ((f > 0 && l < value.matrix[f - 1][p]) || (p > 0 && l < value.matrix[f][p - 1]));
        return <td key={p}>{editable ? <TextField select fullWidth size="small" value={l ?? ''} disabled={busy} error={inconsistent}
          SelectProps={{ displayEmpty: true }} inputProps={{ 'aria-label': `노출 빈도 ${f + 1}단계 사고 가능성 ${p + 1}단계의 종합 가능성` }}
          onChange={event => onChange({ ...value, matrix: value.matrix.map((r, i) => i === f ? r.map((v, j) => j === p ? Number(event.target.value) : v) : r) })}>
          <MenuItem value="" disabled>선택</MenuItem>{[1, 2, 3, 4, 5].map(level => <MenuItem key={level} value={level}>{level}단계 · {likelihoodNames[level - 1]}</MenuItem>)}
        </TextField> : l == null ? '미설정' : `${l}단계`}</td>;
      })}</tr>)}</tbody>
    </table>
    </div>
    <h3>설정 근거</h3>
    {editable ? <TextField fullWidth multiline minRows={2} maxRows={6} value={value.rationale} disabled={busy}
      inputProps={{ maxLength: 2000, 'aria-label': '종합 가능성 설정 근거' }} onChange={event => onChange({ ...value, rationale: event.target.value })} />
      : <p className="fp-rationale">{value.rationale || '등록된 설정 근거가 없습니다.'}</p>}
  </section>;
}
